/**
 * Helpers for Documents factory methods
 */

import type { IBaseNotary } from "../../BaseNotaryFactory";
import type { Document } from "le-coffre-resources/dist/Notary";
import type { ApiPayload, QueryPayload } from "../../../../types";
import type {
	IGetDocumentsparams,
	IGetDocumentByUidParams,
	IPutDocumentsParams,
	IPostDocumentsBatchBody,
	IPostDocumentsBatchResponse,
	IInotPushResponse,
} from "../Documents";
import { createFilesFormData } from "@Front/Api/BaseApiServiceFactory/helpers/formDataRequestHelpers";
import {
	buildDocumentUidsPayload,
	buildDocumentsCreatePayload,
	buildDocumentsBatchPayload,
	buildDocumentsUpdatePayload,
	buildEmptyPayload,
	buildRefusedReasonPayload,
} from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";
import { prepareGetByUidQueryForRequest } from "@Front/Api/helpers/getByUidClientOptionsHelpers";
import { coalesceThrownApiErrorText } from "@Front/Utils/userFacingMessageFromApiClientError";

/**
 * Create GET method for documents
 */
export function createGetMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (q: IGetDocumentsparams): Promise<Document[]> => {
		const url = new URL(baseUrl);
		const query = { q };
		if (q) baseNotary.appendJsonSearchParams(url, query);
		try {
			return await baseNotary.getRequest<Document[]>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create POST method for documents
 */
export function createPostMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (body: ApiPayload): Promise<Document> => {
		const url = new URL(baseUrl);
		try {
			const payload = buildDocumentsCreatePayload(body);
			return await baseNotary.postRequest<Document>(url, payload);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create POST method for multiple documents
 */
export function createPostBatchMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (documents: ApiPayload[]): Promise<IPostDocumentsBatchResponse> => {
		const url = new URL(baseUrl.concat("/batch"));
		try {
			const payload = buildDocumentsBatchPayload(documents) satisfies IPostDocumentsBatchBody;
			return await baseNotary.postRequest<IPostDocumentsBatchResponse>(url, payload);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create GET by UID method for documents
 */
export function createGetByUidMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string, q?: IGetDocumentByUidParams): Promise<Document> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		const { requestQuery, suppressUserFacingErrorOnFailure } = prepareGetByUidQueryForRequest(q as QueryPayload | undefined);
		if (requestQuery) baseNotary.appendJsonSearchParams(url, { q: requestQuery });
		try {
			return await baseNotary.getRequest<Document>(url);
		} catch (err) {
			if (!suppressUserFacingErrorOnFailure) {
				baseNotary.onError(err);
			}
			return Promise.reject(err);
		}
	};
}

/**
 * Create upload document method
 */
export function createUploadDocumentMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (documentUid: string, file: File): Promise<Document> => {
		const url = new URL(baseUrl.concat(`/${documentUid}/upload`));
		const formData = createFilesFormData(file, { normalizeFileName: true });
		try {
			return await baseNotary.postRequestFormData<Document>(url, formData);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create upload documents method (multiple files)
 */
export function createUploadDocumentsMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (documentUid: string, files: File[]): Promise<Document> => {
		const url = new URL(baseUrl.concat(`/${documentUid}/upload`));

		if (!files || files.length === 0) {
			throw new Error("No files provided");
		}

		// Validate all files
		for (const file of files) {
			if (!file || !(file instanceof File)) {
				throw new Error("Invalid file object");
			}
			if (file.size === 0) {
				throw new Error("File is empty");
			}
		}

		const formData = createFilesFormData(files);

		try {
			return await baseNotary.postRequestFormData<Document>(url, formData);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create refuse method
 */
export function createRefuseMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string, refused_reason: string): Promise<Document> => {
		const url = new URL(baseUrl.concat(`/${uid}/refuse`));
		try {
			const payload = buildRefusedReasonPayload(refused_reason);
			return await baseNotary.putRequest<Document>(url, payload);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create PUT method
 */
export function createPutMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string, body: IPutDocumentsParams): Promise<Document> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			const payload = buildDocumentsUpdatePayload(body);
			return await baseNotary.putRequest<Document>(url, payload);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create DELETE method
 */
export function createDeleteMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string): Promise<Document> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			return await baseNotary.deleteRequest<Document>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create resend request method
 */
export function createResendRequestMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string): Promise<{ message: string; document: Document }> => {
		const url = new URL(baseUrl.concat(`/${uid}/resend-request`));
		try {
			return await baseNotary.postRequest<{ message: string; document: Document }>(url, buildEmptyPayload());
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create resend request method for multiple documents
 */
export function createResendRequestBatchMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (documentUids: string[]): Promise<{ message: string; documents: Document[] }> => {
		const url = new URL(baseUrl.concat("/resend-request-batch"));
		try {
			const payload = buildDocumentUidsPayload(documentUids);
			return await baseNotary.postRequest<{ message: string; documents: Document[] }>(url, payload);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create push to iNot method
 */
export function createPushToInotMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string): Promise<IInotPushResponse> => {
		const url = new URL(baseUrl.concat(`/${uid}/push-inot`));
		try {
			return await baseNotary.postRequest<IInotPushResponse>(url, buildEmptyPayload());
		} catch (err) {
			const errorMessage = coalesceThrownApiErrorText(err);
			baseNotary.onError(err);
			if (errorMessage.includes("GENAPI_API_UNAVAILABLE")) {
				return Promise.reject(new Error("GENAPI_API_UNAVAILABLE"));
			}
			return Promise.reject(err);
		}
	};
}
